import { Badge, Skeleton } from "@mantine/core";
import { getCost } from "api";
import Tooltip from "components/Tooltip/Tooltip";
import useMeetingCostRefreshSubscription from "hooks/useMeetingCostRefreshSubscription";
import React, { useMemo } from "react";
import { useQuery } from "react-query";
import { useUser } from "stores/useUser";

const Cost = ({ calendarEvent, start, end }) => {
  const { user } = useUser();

  useMeetingCostRefreshSubscription(
    calendarEvent?.eventId,
    user?.subscription?.organization_id
  );

  const { data, isLoading, isError } = useQuery({
    queryKey: ["Cost", calendarEvent?.eventId],
    queryFn: () => getCost(calendarEvent?.eventId),
    enabled: !!calendarEvent?.eventId,
    retry: false,
  });

  // Calculate values with useMemo before any early returns
  const isExact = data && !data.estimated;
  const { toolTipMsg, backgroundColor, dollarSign } = useMemo(() => {
    if (!data) {
      return {
        toolTipMsg: "Loading...",
        backgroundColor: "gray",
        dollarSign: "...",
      };
    }

    if (isExact) {
      return {
        toolTipMsg: "Exact cost of meeting",
        backgroundColor: "primary",
        dollarSign: `$ ${data?.amount}`,
      };
    } else {
      return {
        toolTipMsg: data?.toolTipMsg,
        backgroundColor: data?.backgroundColor,
        dollarSign: data?.dollarSign,
      };
    }
  }, [data, isExact]);

  // Handle loading state
  if (isLoading) {
    return <Skeleton height={26} width={80} radius="xl" />;
  }

  // Handle error state
  if (isError || !data) {
    return (
     <></>
    );
  }

  const durationMillis = new Date(end) - new Date(start);
  const durationMinutes = Math.max(0, Math.round(durationMillis / 1000 / 60));
  const durationHoursWhole = Math.floor(durationMinutes / 60);
  const durationMinsRemainder = durationMinutes % 60;
  const durationLabel = durationHoursWhole
    ? `${durationHoursWhole}h ${durationMinsRemainder}m`
    : `${durationMinsRemainder}m`;

    return (
    <Tooltip
      style={{
        boxShadow: "0px 0px 8px rgba(0, 0, 0, 0.4)",
      }}
      width={240}
      label={
        <div style={{ padding: 4 }}>
          <div style={{ fontWeight: 600, marginBottom: 2 }}>{dollarSign}</div>
          <div style={{ fontSize: 12, opacity: 0.9 }}>
              {toolTipMsg}
          </div>
          <div style={{ fontSize: 12, opacity: 0.8, marginTop: 6 }}>
            {durationLabel}
          </div>
        </div>
      }
    >
      <Badge
        size="lg"
        sx={{
          background: backgroundColor,
          color: "black",
          cursor: "pointer",
          paddingLeft: 10,
          paddingRight: 10,
          boxShadow: "0 1px 0 rgba(0,0,0,0.08)",
          transition: "transform 120ms ease, box-shadow 120ms ease",
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          },
        }}
      >
        <span title={isExact ? "Exact cost" : "Estimated cost"}>
          {dollarSign}
        </span>
      </Badge>
    </Tooltip>
  );
};

export default Cost;
